import { AEMLink, ChildInput } from '@msc-b2c/interfaces/types';
import { IPublicClientApplication } from '@azure/msal-browser';
import {
  AddressContactInformationI,
  AzureB2CConfig,
  GetContactInformationMyMSCResponse,
  MarketingConsentsContactInformationI,
} from '@msc-b2c/interfaces/interfaces';

import { getLoginRedirectConfig } from '../../utils/loginUtils';
import { convertPathToDotNotation, FormTypeEnum } from '@msc-b2c/shared-utils';

/**
 * Builds the per-channel consent object to send to the API from a single FE checkbox value.
 *
 * Rules (applied independently for marketingConsents and profilingConsents):
 *  - Touched & true  → true only for channels that have data in the final request body
 *  - Touched & false → all channels false
 *  - Not touched     → if at least one existing per-channel consent is true, mirror
 *                       channel availability from the request body; otherwise keep all false.
 *                       If existingConsents is undefined the field is omitted (returns undefined).
 *
 * @param isTouched        Whether the user interacted with the consent checkbox
 * @param consentValue     The value of the checkbox (true / false / undefined when not touched)
 * @param existingConsents The per-channel consents already stored in the user profile
 * @param hasEmail         Whether the request body contains a non-empty email
 * @param hasPhone         Whether the request body contains a non-empty phone
 * @param hasMobilePhone   Whether the request body contains a non-empty mobilePhone
 */
export function buildConsentPerChannel(
  isTouched: boolean,
  consentValue: boolean | undefined,
  existingConsents: MarketingConsentsContactInformationI | undefined,
  hasEmail: boolean,
  hasPhone: boolean,
  hasMobilePhone: boolean
): MarketingConsentsContactInformationI | undefined {
  if (isTouched) {
    if (consentValue === true) {
      return { email: hasEmail, phone: hasPhone, mobilePhone: hasMobilePhone };
    }
    return { email: false, phone: false, mobilePhone: false };
  }

  if (existingConsents === undefined || existingConsents === null) {
    return undefined;
  }

  const atLeastOneTrue =
    !!existingConsents.email || !!existingConsents.phone || !!existingConsents.mobilePhone;

  if (atLeastOneTrue) {
    return { email: hasEmail, phone: hasPhone, mobilePhone: hasMobilePhone };
  }

  return { email: false, phone: false, mobilePhone: false };
}

/** RHF field names like `address.country` are nested paths, not a single object key. */
function setNestedFormDefault(target: Record<string, unknown>, path: string, value: unknown): void {
  const keys = path.split('.');
  if (keys.length === 1) {
    target[path] = value;
    return;
  }
  let cur: Record<string, unknown> = target;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    const next = cur[k];
    if (next == null || typeof next !== 'object' || Array.isArray(next)) {
      cur[k] = {};
    }
    cur = cur[k] as Record<string, unknown>;
  }
  cur[keys[keys.length - 1]] = value;
}

/**
 * Handles the password management call-to-action by redirecting to the password reset flow.
 *
 * @param ctaLink - The AEM link object that contains information about the call-to-action
 * @param azureConfig - The Azure B2C configuration object
 * @param instance - The MSAL public client application instance
 * @param fullLoginAuthenticationExchangePage - The full URL of the authentication exchange page
 */
export async function handlePasswordManagementCta(
  ctaLink: AEMLink,
  azureConfig: AzureB2CConfig,
  instance: IPublicClientApplication,
  fullLoginAuthenticationExchangePage: string
): Promise<void> {
  if (!ctaLink?.valid || !instance || !azureConfig) return;

  const resetPasswordRequest = getLoginRedirectConfig(
    azureConfig,
    'resetPassword',
    fullLoginAuthenticationExchangePage
  );

  try {
    await instance.loginRedirect(resetPasswordRequest);
  } catch (error) {
    console.error('Password reset error:', error);
  }
}

/**
 * Processes form fields and customer information to generate default values for form inputs.
 *
 * @param customerInfo - Optional response object containing customer contact information
 * @param formFields - Optional array of form field definitions
 * @returns {Object} An object containing default values for form fields, with field names as keys
 */
export function getFormInputsDefaultValues(
  customerInfo?: GetContactInformationMyMSCResponse,
  formFields?: ChildInput[]
): { [key: string]: any } {
  const defaultValues: {
    [key: string]:
      | string
      | number
      | boolean
      | readonly string[]
      | MarketingConsentsContactInformationI
      | AddressContactInformationI
      | undefined;
  } = {};
  if (
    !customerInfo ||
    Object.keys(customerInfo).length === 0 ||
    !formFields ||
    formFields.length === 0
  )
    return defaultValues;

  formFields.forEach((child) => {
    if (child?.name?.includes('marketingConsent')) {
      setNestedFormDefault(
        defaultValues,
        child.name,
        !!customerInfo?.marketingConsents?.phone ||
          !!customerInfo?.marketingConsents?.mobilePhone ||
          !!customerInfo?.marketingConsents?.email
      );
    } else if (child?.name?.includes('profilingConsents')) {
      setNestedFormDefault(
        defaultValues,
        child.name,
        !!customerInfo?.profilingConsents?.phone ||
          !!customerInfo?.profilingConsents?.mobilePhone ||
          !!customerInfo?.profilingConsents?.email
      );
    } else if (child.name && child.name in customerInfo && !child.readOnly) {
      const raw = customerInfo[child.name as keyof typeof customerInfo];
      const normalized =
        child.type === FormTypeEnum.EMAIL && typeof raw === 'string' ? raw.toLowerCase() : raw;
      setNestedFormDefault(defaultValues, child.name, normalized);
    } else if (
      !child.readOnly &&
      (child.type === FormTypeEnum.EMAIL ||
        child.type === FormTypeEnum.PHONE ||
        child.type === FormTypeEnum.TEXT ||
        child.type === FormTypeEnum.DATE)
    ) {
      const value = convertPathToDotNotation(customerInfo, child.name);
      const normalized =
        child.type === FormTypeEnum.EMAIL && typeof value === 'string'
          ? value.toLowerCase()
          : value;
      setNestedFormDefault(defaultValues, child.name, normalized ?? '');
    } else if (!child.readOnly && child.type === FormTypeEnum.ADDRESS_SEARCH) {
      if (child.name) {
        const v = convertPathToDotNotation(customerInfo, child.name);
        if (v != null && v !== '') setNestedFormDefault(defaultValues, child.name, v);
      }
      if (child.secondName) {
        const v = convertPathToDotNotation(customerInfo, child.secondName);
        if (v != null && v !== '') setNestedFormDefault(defaultValues, child.secondName, v);
      }
    } else if (
      child.type === FormTypeEnum.DROPDOWN &&
      (child.name === 'address.country' || child.name === 'address.countryCode')
    ) {
      const value = convertPathToDotNotation(customerInfo, 'address.country') ?? child.value;
      const dropdownItem = child.dropdownItems?.find(
        (item) =>
          value &&
          (item.text.toLowerCase() === String(value).toLowerCase() ||
            item.value === value ||
            item.value === String(value).toUpperCase())
      );
      setNestedFormDefault(defaultValues, child.name, dropdownItem?.value ?? '');
    } else if (
      !child.readOnly &&
      child.type === FormTypeEnum.DROPDOWN &&
      child.name?.startsWith('address.') &&
      child.name !== 'address.country' &&
      child.name !== 'address.countryCode'
    ) {
      const value = convertPathToDotNotation(customerInfo, child.name);
      if (value != null && value !== '') {
        const dropdownItem = child.dropdownItems?.find(
          (item) => item.value === value || item.text?.toLowerCase() === String(value).toLowerCase()
        );
        if (dropdownItem) {
          setNestedFormDefault(defaultValues, child.name, dropdownItem.value);
        } else {
          setNestedFormDefault(defaultValues, child.name, value);
        }
      }
    } else if (child.readOnly && child.type === FormTypeEnum.CHECKBOX) {
      setNestedFormDefault(
        defaultValues,
        child.name,
        child.items?.some((item) => item.value === child.name && item.selected)
      );
    }
  });

  return defaultValues;
}
